import React, { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';
import { SmsGateContext } from '../context/SmsGateContext';
import { SMS_GATE_DOC, smsAvailableFrom } from '../domain/smsGate';

// The listener behind SmsGateContext (#782): one document outside any studio,
// `deployment/messaging`, which only the server writes (functions/delivery/smsGate.js).
//
// A plain `onSnapshot` rather than the read store: the store, the queue and
// the window hooks all assume `users/{uid}/{collection}`, and this document is
// nobody's. It is one small read per session, not a window.
//
// `includeMetadataChanges`, because a reading from the cache counts as off
// (`smsAvailableFrom`), and without it the server confirming what the cache
// already said is a metadata-only change the listener would never deliver —
// the controls would stay off for a session after they should have come on.
// The cost is a callback when the connection drops or returns, which is the
// moment the answer should change.
//
// Signed out, unconfigured, refused, failed: off. A refusal is logged and not
// retried; the next sign-in listens again.
export function SmsGateProvider({ children }) {
  const { user } = useAuth();
  const uid = user?.uid || null;
  const [reading, setReading] = useState({ uid: null, available: false });

  useEffect(() => {
    if (!uid || !db) return undefined;
    return onSnapshot(
      doc(db, SMS_GATE_DOC.collection, SMS_GATE_DOC.id),
      { includeMetadataChanges: true },
      (snap) => setReading({
        uid,
        available: smsAvailableFrom({
          exists: snap.exists(), data: snap.data() || null, fromCache: snap.metadata.fromCache,
        }),
      }),
      (err) => {
        console.warn('[sms] could not read whether texts can be sent; SMS controls stay off:', err);
        setReading({ uid, available: false });
      },
    );
  }, [uid]);

  // Whose reading it is, so a sign-out or a second account never inherits one.
  const available = Boolean(uid) && reading.uid === uid && reading.available;
  return <SmsGateContext.Provider value={available}>{children}</SmsGateContext.Provider>;
}
